import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SETTINGS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import {
  CheckCircle2,
  AlertCircle,
  Sparkles,
  RefreshCcw,
  ExternalLink,
  Plus,
  Trash2,
  Save,
  Bot,
  DollarSign,
  Tag,
  Zap,
} from "lucide-react";

const STATUS_STYLES = {
  green: { pill: "pill-interested", accent: "#00C781" },
  amber: { pill: "pill-website_sent", accent: "#B45309" },
  red: { pill: "pill-closed_lost", accent: "#B91C1C" },
  gray: { pill: "pill-new", accent: "#6B7280" },
};

function A2PStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    setRefreshing(force);
    setError(null);
    try {
      const { data } = await api.get(
        `/settings/twilio/a2p-status${force ? "?refresh=true" : ""}`
      );
      setStatus(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return (
      <div className="bg-white border border-neutral-200 p-6 text-neutral-500 text-sm">
        Checking A2P registration…
      </div>
    );

  if (error) {
    return (
      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-2">A2P 10DLC status</div>
        <div className="text-sm text-neutral-500">{error}</div>
      </div>
    );
  }

  const style = STATUS_STYLES[status.color] || STATUS_STYLES.gray;

  return (
    <div
      data-testid="settings-a2p-container"
      className="bg-white border border-neutral-200 p-6 relative overflow-hidden"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-2">Twilio · A2P 10DLC registration</div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span
              data-testid="settings-a2p-status-pill"
              className={`pill ${style.pill}`}
            >
              {status.label}
            </span>
            <span className="text-xs font-mono text-neutral-500">
              From {status.twilio_from}
            </span>
            {status.account_type && (
              <span
                className={`pill ${
                  status.account_type === "Full" ? "pill-interested" : "pill-website_sent"
                } text-[10px]`}
                data-testid="settings-a2p-account-type"
              >
                {status.account_type} account
                {status.balance ? ` · ${status.balance}` : ""}
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-600 max-w-2xl">
            {status.message}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="settings-a2p-refresh-btn"
            className="lf-btn-outline text-xs inline-flex items-center gap-1.5"
            title="Force refresh from Twilio"
          >
            <RefreshCcw
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Checking…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Brands */}
      <div className="mt-6">
        <div className="overline mb-3">
          Brands ({status.brands?.length || 0})
        </div>
        {status.brands?.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-4 text-xs text-neutral-500">
            No Brand registered. Start at{" "}
            <a
              href="https://console.twilio.com/us1/service/sms/messaging-services"
              target="_blank"
              rel="noreferrer"
              className="border-b border-black font-medium"
            >
              Twilio → Messaging Services ↗
            </a>{" "}
            → create a service → add your number → then register brand + campaign.
          </div>
        ) : (
          <div className="grid gap-2">
            {status.brands.map((b) => (
              <div
                key={b.sid}
                data-testid={`settings-a2p-brand-${b.sid}`}
                className="border border-neutral-200 p-3 flex items-center justify-between text-sm"
              >
                <div>
                  <div className="font-semibold">
                    {b.brand_type || "Brand"}{" "}
                    <span className="text-xs font-mono text-neutral-500 ml-1">
                      {b.sid}
                    </span>
                  </div>
                  {b.failure_reason && (
                    <div className="text-xs text-red-700 mt-1">
                      Reason: {b.failure_reason}
                    </div>
                  )}
                  <div className="text-xs text-neutral-400 mt-1">
                    Updated {b.date_updated?.slice(0, 10)}
                  </div>
                </div>
                <span
                  className={`pill ${
                    b.status === "APPROVED"
                      ? "pill-interested"
                      : b.status === "FAILED"
                      ? "pill-closed_lost"
                      : "pill-website_sent"
                  }`}
                >
                  {b.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaigns */}
      <div className="mt-6">
        <div className="overline mb-3">
          Campaigns ({status.campaigns?.length || 0})
        </div>
        {status.campaigns?.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-4 text-xs text-neutral-500 space-y-2">
            <div>
              No Campaign registered.{" "}
              <a
                href="https://console.twilio.com/us1/service/sms/messaging-services"
                target="_blank"
                rel="noreferrer"
                className="border-b border-black font-medium inline-flex items-center gap-1"
              >
                Open Messaging Services in Twilio <ExternalLink className="w-3 h-3" />
              </a>
              <ol className="list-decimal pl-4 mt-2 space-y-1">
                <li>Click your Messaging Service to open it</li>
                <li>Click the <strong>COMPLIANCE</strong> tab at the top (not the sidebar)</li>
                <li>Click <strong>Register a Campaign</strong> → fill the form</li>
                <li>When it asks for Privacy Policy + Terms URLs, use the two below</li>
              </ol>
            </div>
            <div className="text-neutral-500 pt-2 border-t border-neutral-200">
              <strong>Required URLs</strong> (both are already live in this app —
              just paste them in the Twilio form):
              <div className="mt-1 space-y-1">
                <div>
                  Privacy Policy:{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-black border-b border-black break-all"
                  >
                    {window.location.origin}/privacy
                  </a>
                </div>
                <div>
                  Terms & Conditions:{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-black border-b border-black break-all"
                  >
                    {window.location.origin}/terms
                  </a>
                </div>
              </div>
            </div>
            <div className="text-amber-800 pt-2 border-t border-neutral-200">
              {status.account_type === "Trial" ? (
                <>⚠️ <strong>Trial account detected</strong> — Twilio blocks A2P
                registration for trial accounts. Upgrade first by adding funds.</>
              ) : status.account_type === "Full" ? (
                <>✅ Full account detected — you can register the campaign now.</>
              ) : (
                <>Checking account type…</>
              )}
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            {status.campaigns.map((c) => (
              <div
                key={c.sid}
                data-testid={`settings-a2p-campaign-${c.sid}`}
                className="border border-neutral-200 p-3 flex items-center justify-between text-sm"
              >
                <div className="flex-1 mr-4">
                  <div className="font-semibold">
                    {c.service_name}
                    <span className="text-xs font-mono text-neutral-500 ml-2">
                      {c.use_case}
                    </span>
                  </div>
                  {c.description && (
                    <div className="text-xs text-neutral-500 mt-1">
                      {c.description}
                    </div>
                  )}
                </div>
                <span
                  className={`pill ${
                    ["VERIFIED", "APPROVED"].includes(c.campaign_status)
                      ? "pill-interested"
                      : ["FAILED", "REJECTED"].includes(c.campaign_status)
                      ? "pill-closed_lost"
                      : "pill-website_sent"
                  }`}
                >
                  {c.campaign_status || "UNKNOWN"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 text-xs font-mono text-neutral-400">
        {status.cached ? "Cached (auto-refresh every 60s)" : "Just refreshed"} ·{" "}
        {status.fetched_at?.slice(0, 19).replace("T", " ")} UTC
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-0.5"
        style={{ background: style.accent }}
      />
    </div>
  );
}

function PricingEditor() {
  const [plans, setPlans] = useState([]);
  const [stripeStatus, setStripeStatus] = useState({});     // { [key]: {status, warning, display_price, stripe_price, lookup_key} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingKey, setSyncingKey] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const { user: me } = useAuth();
  const isAdmin = (me?.role || "") === "admin";

  const loadStripeStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/pricing/stripe-sync-status");
      const map = {};
      (data.rows || []).forEach((r) => { map[r.key] = r; });
      setStripeStatus(map);
    } catch { /* silent — only matters when admin wants Stripe sync visible */ }
  }, []);

  const syncAllMismatched = async () => {
    const toSync = Object.values(stripeStatus).filter(
      (r) => (r.status === "mismatch" || r.status === "missing") && Number(r.display_price) > 0
    );
    if (!toSync.length) { toast.message("Nothing to sync — all plans match Stripe."); return; }
    if (!window.confirm(
      `Sync ${toSync.length} plan(s) to Stripe at your admin prices?\n\n` +
      toSync.map((r) => `  • ${r.name} → $${r.display_price}`).join("\n") +
      `\n\nExisting subscribers stay grandfathered. New checkouts use the new prices.`
    )) return;
    setSyncingAll(true);
    let ok = 0; let failed = 0;
    for (const row of toSync) {
      try {
        await api.post(`/pricing/stripe-sync/${row.key}`);
        ok += 1;
      } catch { failed += 1; }
    }
    setSyncingAll(false);
    await loadStripeStatus();
    if (failed === 0) toast.success(`All ${ok} plans synced to Stripe.`);
    else              toast.error(`Synced ${ok} · failed ${failed}. Scroll down for per-plan errors.`);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pricing");
      setPlans(data.plans || []);
      await loadStripeStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [loadStripeStatus]);
  useEffect(() => { load(); }, [load]);

  const upd = (idx, field, value) =>
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));

  const updBullet = (idx, bIdx, value) =>
    setPlans((prev) => prev.map((p, i) => i === idx
      ? { ...p, bullets: p.bullets.map((b, j) => j === bIdx ? value : b) }
      : p));
  const addBullet = (idx) =>
    setPlans((prev) => prev.map((p, i) => i === idx ? { ...p, bullets: [...(p.bullets || []), ""] } : p));
  const rmBullet = (idx, bIdx) =>
    setPlans((prev) => prev.map((p, i) => i === idx
      ? { ...p, bullets: p.bullets.filter((_, j) => j !== bIdx) }
      : p));

  const setBest = (idx) =>
    setPlans((prev) => prev.map((p, i) => ({ ...p, best: i === idx })));

  const addPlan = () => {
    const key = `plan_${Date.now()}`;
    setPlans((prev) => [...prev, {
      key, name: "New Plan", tag: "", price: 0, best: false,
      bullets: ["Feature one"], lookup_key: key,
    }]);
  };

  const rmPlan = (idx) => {
    if (!window.confirm("Remove this plan?")) return;
    setPlans((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!plans.length) { toast.error("Keep at least one plan."); return; }
    setSaving(true);
    try {
      const cleaned = plans.map((p) => ({
        ...p,
        price: Number(p.price) || 0,
        bullets: (p.bullets || []).filter((b) => b && b.trim()),
      }));
      const { data } = await api.put("/pricing", { plans: cleaned });
      setPlans(data.plans);
      // Read back from public endpoint to prove persistence + surface any
      // silent proxy/env-DB mismatch (the classic "why isn't it reflecting?" trap).
      try {
        const { data: pub } = await api.get(`/public/site-config?t=${Date.now()}`);
        const persistedPrices = pub.plans.map((p) => `${p.key}=$${p.price}`).join(", ");
        toast.success(`Saved & confirmed live: ${persistedPrices}`);
      } catch {
        toast.success("Pricing saved. Landing page will reflect on next visit.");
      }
      // Refresh Stripe sync status so any newly-created mismatches surface immediately.
      await loadStripeStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Loading pricing…</div>;

  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid="settings-pricing-editor">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Public pricing
          </div>
          <div className="font-display text-2xl font-bold">Plans shown on the landing page</div>
          <p className="text-sm text-neutral-500 mt-1">
            Changes appear on <a href="/" target="_blank" rel="noreferrer" className="border-b border-black">clientsonramp.com</a> immediately after save. Also feeds the billing page.
          </p>
          <p className="text-[11px] font-mono mt-2 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 inline-block">
            Preview and production have SEPARATE databases — save on prod's admin console to update prod's landing page.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="settings-pricing-add-plan"
            onClick={addPlan}
            className="lf-btn-outline flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add plan
          </button>
          {Object.values(stripeStatus).some((r) => r.status === "mismatch" || r.status === "missing") && (
            <button
              data-testid="settings-pricing-sync-all"
              onClick={syncAllMismatched}
              disabled={syncingAll}
              className="lf-btn-outline flex items-center gap-1 border-amber-500 text-amber-800 hover:bg-amber-50"
            >
              <Zap className="w-4 h-4" /> {syncingAll ? "Syncing all…" : "Sync all to Stripe"}
            </button>
          )}
          <button
            data-testid="settings-pricing-save"
            onClick={save}
            disabled={saving}
            className="lf-btn-primary flex items-center gap-1"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {plans.map((p, idx) => {
          const sync = stripeStatus[p.key];
          const syncBg = {
            match:                 { bg: "#D1FAE5", fg: "#065F46", label: "Stripe matches" },
            mismatch:              { bg: "#FEE2E2", fg: "#991B1B", label: "Stripe mismatch" },
            missing:               { bg: "#FEF3C7", fg: "#92400E", label: "Stripe missing" },
            stripe_not_configured: { bg: "#E5E7EB", fg: "#525252", label: "Stripe not set up" },
            error:                 { bg: "#FCE7F3", fg: "#9D174D", label: "Stripe error" },
            unknown:               { bg: "#F3F4F6", fg: "#525252", label: "—" },
          }[sync?.status || "unknown"];
          const canSync = sync && (sync.status === "mismatch" || sync.status === "missing") && (Number(p.price) > 0);
          const syncThisPlan = async () => {
            if (!window.confirm(
              `Create a new Stripe price for '${p.lookup_key || p.key + "_monthly"}' at $${Number(p.price)}/mo?\n\nExisting subscribers stay grandfathered on the old price. New checkouts use the new price.`
            )) return;
            setSyncingKey(p.key);
            try {
              const { data } = await api.post(`/pricing/stripe-sync/${p.key}`);
              toast.success(`Synced ${p.name} to Stripe: $${data.amount}/mo (price=${data.new_price_id.slice(0, 10)}…)`);
              await loadStripeStatus();
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setSyncingKey(null); }
          };
          return (
          <div
            key={p.key}
            data-testid={`settings-plan-${p.key}`}
            className={`border p-4 ${p.best ? "border-black" : "border-neutral-200"}`}
          >
            {/* Stripe sync status pill + action */}
            {sync && (
              <div className="mb-3 -mx-4 -mt-4 px-4 py-2 border-b border-neutral-200 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span data-testid={`settings-plan-${p.key}-stripe-pill`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5" style={{ background: syncBg.bg, color: syncBg.fg }}>
                    {syncBg.label}
                  </span>
                  {sync.stripe_price != null && (
                    <span className="text-[10px] font-mono text-neutral-500">
                      Stripe: ${sync.stripe_price} · Display: ${sync.display_price}
                    </span>
                  )}
                </div>
                {canSync && (
                  <button
                    data-testid={`settings-plan-${p.key}-stripe-sync`}
                    onClick={syncThisPlan}
                    disabled={syncingKey === p.key || !isAdmin}
                    className="text-[11px] font-semibold px-2 py-1 border border-neutral-900 hover:bg-neutral-900 hover:text-white disabled:opacity-50"
                  >
                    {syncingKey === p.key ? "Syncing…" : "Sync to Stripe"}
                  </button>
                )}
              </div>
            )}
            {sync?.warning && (sync.status === "mismatch" || sync.status === "missing") && (
              <div className="mb-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5 leading-snug" data-testid={`settings-plan-${p.key}-stripe-warning`}>
                {sync.warning}
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="best-plan"
                  checked={!!p.best}
                  onChange={() => setBest(idx)}
                  data-testid={`settings-plan-${p.key}-best`}
                />
                <span className="text-xs uppercase tracking-widest text-neutral-500">
                  {p.best ? "Highlighted" : "Highlight"}
                </span>
              </label>
              <button
                onClick={() => rmPlan(idx)}
                className="text-neutral-400 hover:text-red-600 transition"
                data-testid={`settings-plan-${p.key}-delete`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>

            <label className="block mb-2">
              <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Name</span>
              <input
                data-testid={`settings-plan-${p.key}-name`}
                value={p.name || ""}
                onChange={(e) => upd(idx, "name", e.target.value)}
                className="lf-input"
              />
            </label>
            <label className="block mb-2">
              <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Tag</span>
              <input
                value={p.tag || ""}
                onChange={(e) => upd(idx, "tag", e.target.value)}
                className="lf-input"
                placeholder="Website + AI chat agent"
              />
            </label>
            <label className="block mb-2">
              <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Monthly price ($)</span>
              <input
                data-testid={`settings-plan-${p.key}-price`}
                type="number"
                min="0"
                value={p.price}
                onChange={(e) => upd(idx, "price", e.target.value)}
                className="lf-input font-mono"
              />
            </label>
            <label className="block mb-2">
              <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Stripe lookup key</span>
              <input
                value={p.lookup_key || ""}
                onChange={(e) => upd(idx, "lookup_key", e.target.value)}
                className="lf-input font-mono text-xs"
                placeholder="starter_monthly"
              />
            </label>

            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold tracking-widest uppercase text-neutral-500">Bullets</span>
                <button
                  onClick={() => addBullet(idx)}
                  className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> add
                </button>
              </div>
              <div className="space-y-1">
                {(p.bullets || []).map((b, bIdx) => (
                  <div key={bIdx} className="flex gap-1">
                    <input
                      value={b}
                      onChange={(e) => updBullet(idx, bIdx, e.target.value)}
                      className="lf-input text-sm"
                      placeholder="Feature detail…"
                    />
                    <button
                      onClick={() => rmBullet(idx, bIdx)}
                      className="text-neutral-400 hover:text-red-600 px-2"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function PublicAgentPanel() {
  const [config, setConfig] = useState(null);
  const [agents, setAgents] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const { user: me } = useAuth();
  const isAdmin = (me?.role || "") === "admin";

  const load = useCallback(async () => {
    try {
      const [{ data: cfg }, { data: ags }] = await Promise.all([
        api.get("/settings/app-config"),
        api.get("/agents"),
      ]);
      setConfig(cfg);
      setKeywords((cfg.auto_promote_keywords || []).join(", "));
      setAgents(ags.agents || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/app-config", {
        public_agent_id: config.public_agent_id,
        auto_promote_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      setConfig(data);
      setKeywords((data.auto_promote_keywords || []).join(", "));
      toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const bootstrap = async () => {
    setBootstrapping(true);
    try {
      const { data } = await api.post("/settings/public-agent/bootstrap");
      toast.success(data.created ? "ClientsOnramp Concierge created & set as public agent." : "Public agent already exists — reusing.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBootstrapping(false); }
  };

  if (!config) return <div className="text-neutral-500 text-sm">Loading…</div>;

  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid="settings-app-config">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-1">
            <Bot className="w-3 h-3" /> Public site
          </div>
          <div className="font-display text-2xl font-bold">Landing page AI & inbound routing</div>
          <p className="text-sm text-neutral-500 mt-1">
            Which AI agent visitors talk to on your landing, plus keywords that
            auto-promote inbound form submissions straight into the lead pipeline.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="settings-save-app-config"
            onClick={save}
            disabled={saving}
            className="lf-btn-primary flex items-center gap-1"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {!isAdmin && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 mb-4">
          Read-only — admin role required to edit.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div>
          <label className="block">
            <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">Public AI agent</span>
            <select
              data-testid="settings-public-agent-select"
              value={config.public_agent_id || ""}
              onChange={(e) => setConfig({ ...config, public_agent_id: e.target.value })}
              className="lf-input"
            >
              <option value="">— None (widget hidden) —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.lead_id ? "(lead-scoped)" : "(global)"}
                </option>
              ))}
            </select>
          </label>
          {!config.public_agent && (
            <button
              data-testid="settings-bootstrap-public-agent"
              onClick={bootstrap}
              disabled={bootstrapping}
              className="mt-3 lf-btn-outline text-xs flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              {bootstrapping ? "Creating…" : "Bootstrap ClientsOnramp Concierge"}
            </button>
          )}
          {config.public_agent && (
            <div className="mt-3 text-xs bg-neutral-50 border border-neutral-200 p-3">
              <div className="font-semibold mb-1">{config.public_agent.name}</div>
              <div className="text-neutral-600 italic">&quot;{config.public_agent.greeting?.slice(0, 140)}{config.public_agent.greeting?.length > 140 ? "…" : ""}&quot;</div>
              <div className="mt-3 flex gap-3 text-xs">
                <a
                  href={`/agents/${config.public_agent_id}`}
                  data-testid="settings-edit-concierge-link"
                  className="font-medium underline underline-offset-2 hover:text-black text-neutral-700 inline-flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" /> Tune the pitch (edit FAQs, tone, services)
                </a>
                <a
                  href="/widget-chats"
                  className="font-medium underline underline-offset-2 hover:text-black text-neutral-700 inline-flex items-center gap-1"
                >
                  View conversations →
                </a>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block">
            <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2 flex items-center gap-1">
              <Tag className="w-3 h-3" /> Auto-promote keywords
            </span>
            <textarea
              data-testid="settings-keywords-input"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              rows={4}
              className="lf-input font-mono text-xs"
              placeholder="ready to sign, pricing, urgent, quote"
            />
          </label>
          <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
            Comma-separated. If any of these words appear in an inbound contact-form
            message, business name, or industry, the submission is <strong>auto-promoted</strong> into your leads pipeline immediately.
          </p>
        </div>
      </div>
    </div>
  );
}


export default function Settings() {
  const [integrations, setIntegrations] = useState([]);
  const [dirEnabled, setDirEnabled] = useState(true);

  useEffect(() => {
    api.get("/settings/integrations")
      .then(({ data }) => setIntegrations(data.integrations))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/public/directory-config")
      .then(({ data }) => setDirEnabled(data.enabled !== false))
      .catch(() => {});
  }, []);

  const toggleDirectory = async () => {
    const next = !dirEnabled;
    try {
      await api.post("/settings/directory", { enabled: next });
      setDirEnabled(next);
      toast.success(next ? "Directory is now visible to visitors" : "Directory hidden — visitors see 'Coming soon'");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const twilioIntegration = integrations.find((i) => i.key === "twilio");
  return (
    <div data-testid={SETTINGS.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-10 max-w-2xl">
        <div className="overline mb-3">Back office</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
          Integrations & settings
        </h1>
        <p className="text-neutral-500">
          Snapshot of every connected service. Green = live and connected. Amber
          = key required in <span className="font-mono">backend/.env</span>.
        </p>
      </div>

      <SmsStatusBanner />

      {/* DB-MANAGED DEPLOYMENT SETTINGS — Calendly URL, admin email, etc.
          Editable here without needing the Emergent env-var editor. */}
      <DeploymentSettingsCard />

      {/* Directory visibility toggle */}
      <div className="bg-white border border-neutral-200 p-5 mb-8 flex items-center justify-between">
        <div>
          <div className="overline mb-1">Public directory</div>
          <div className="font-semibold">Show the client directory to public visitors</div>
          <p className="text-sm text-neutral-500 mt-1 max-w-xl">
            When off, /directory shows a &quot;Coming soon&quot; page. Auto-gate also hides it until you have 10+ clients — recommended until you build up social proof.
          </p>
        </div>
        <button
          onClick={toggleDirectory}
          data-testid="settings-directory-toggle"
          className={`px-4 py-2 border font-semibold text-sm ${dirEnabled ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-neutral-100 border-neutral-300 text-neutral-700"}`}
        >
          {dirEnabled ? "✅ Visible" : "🚫 Hidden"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {integrations.map((i) => (
          <div
            key={i.key}
            data-testid={SETTINGS.card(i.key)}
            className="bg-white border border-neutral-200 p-6 relative"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline mb-1">{i.purpose}</div>
                <div className="font-display text-xl font-semibold">
                  {i.name}
                </div>
              </div>
              {i.configured ? (
                <span className="pill pill-interested inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Live
                </span>
              ) : (
                <span className="pill pill-website_sent inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Missing
                </span>
              )}
            </div>
            <div className="text-xs font-mono text-neutral-500 border-t border-neutral-100 pt-3">
              {i.env_key}
            </div>
            <div
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ background: i.configured ? "#00C781" : "#B45309" }}
            />
          </div>
        ))}
      </div>

      {/* A2P monitor — only relevant if Twilio is configured */}
      {twilioIntegration?.configured && (
        <div className="mb-8">
          <A2PStatus />
        </div>
      )}

      <div className="mb-6">
        <PublicAgentPanel />
      </div>

      <div className="mb-8">
        <PricingEditor />
      </div>

      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Admin
        </div>
        <div className="font-display text-2xl font-bold mb-3">System info</div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-3 text-sm">
          <dt className="text-neutral-500">App</dt>
          <dd className="font-mono">ClientsOnramp Console v2</dd>
          <dt className="text-neutral-500">Auth</dt>
          <dd className="font-mono">JWT (HS256) · Bearer + httpOnly cookie</dd>
          <dt className="text-neutral-500">Payment mode</dt>
          <dd className="font-mono">Stripe test</dd>
          <dt className="text-neutral-500">LLM</dt>
          <dd className="font-mono">Claude Sonnet 4.6 (Emergent LLM proxy)</dd>
        </dl>
      </div>
    </div>
  );
}

function DeploymentSettingsCard() {
  const FIELDS = [
    { key: "BOOKING_URL", label: "Booking URL (Calendly / Cal.com)", placeholder: "https://calendly.com/yourhandle/20min", help: "Where the 'Schedule my 20-min call' button on public audit reports and reactivation autoresponder emails link to." },
    { key: "BOOKING_EMAIL", label: "Booking / contact email", placeholder: "you@yourbusiness.com", help: "Shown on public audit reports next to the Calendly button." },
    { key: "ADMIN_EMAIL", label: "Admin email", placeholder: "you@yourbusiness.com", help: "Receives new-lead notifications from the reactivation form and other public submits." },
    { key: "BACKUP_NOTIFY_EMAIL", label: "Weekly backup notification email", placeholder: "you@yourbusiness.com (falls back to admin email)", help: "Where the weekly encrypted backup email lands. Defaults to Admin email if blank." },
  ];
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/deployment");
      setRows(data.settings);
      const d = {};
      Object.entries(data.settings).forEach(([k, v]) => { d[k] = v.db_value || ""; });
      setDraft(d);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => { payload[key] = (draft[key] || "").trim(); });
      const { data } = await api.put("/settings/deployment", payload);
      setRows(data.settings);
      toast.success("Saved — new values are live immediately (no redeploy needed).");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!rows) {
    return (
      <div className="bg-white border border-neutral-200 p-6 mb-8 text-sm text-neutral-500" data-testid="deployment-settings-loading">
        Loading deployment settings…
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-emerald-300 border-l-8 border-l-emerald-500 p-6 mb-8" data-testid="deployment-settings-card">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="overline mb-1 text-emerald-700">Deployment settings</div>
          <div className="font-display text-2xl font-bold">Live-editable env values</div>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            Set your Calendly URL, admin email, and other deployment vars right here — no env-var editor, no redeploy required. Values save instantly. If a platform env var of the same name is also set, the DB value here wins.
          </p>
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="deployment-settings-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50 whitespace-nowrap"
        >
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-5">
        {FIELDS.map((f) => {
          const row = rows[f.key] || {};
          const source = row.source;
          return (
            <div key={f.key} data-testid={`deployment-settings-row-${f.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-neutral-800">{f.label}</label>
                <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 border ${
                  source === "db" ? "text-emerald-700 border-emerald-300 bg-emerald-50" :
                  source === "env" ? "text-amber-700 border-amber-300 bg-amber-50" :
                  "text-neutral-500 border-neutral-200 bg-neutral-50"
                }`}>
                  {source === "db" ? "· DB" : source === "env" ? "· ENV" : "· UNSET"}
                </span>
              </div>
              <input
                data-testid={`deployment-settings-input-${f.key}`}
                value={draft[f.key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full px-3 py-2.5 border border-neutral-300 text-sm font-mono focus:border-emerald-500 focus:outline-none"
              />
              <p className="text-xs text-neutral-500 mt-1.5 leading-snug">{f.help}</p>
              {source === "env" && !row.db_value && (
                <p className="text-[11px] text-amber-700 mt-1 font-mono">Currently using ENV value: <span className="opacity-75">{row.env_value}</span></p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Prominent Twilio A2P 10DLC status banner shown at the top of Settings.
 * States: loading · pending (in review) · blocked (missing/rejected) · live.
 * Non-blocking — pulls from the existing /settings/twilio/a2p-status endpoint.
 */
function SmsStatusBanner() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings/twilio/a2p-status").then(({ data }) => setS(data)).catch(() => setS({ overall: "unknown" }));
  }, []);

  if (!s) return null;

  const state = deriveState(s);
  if (state.kind === "live") {
    return (
      <div data-testid="sms-banner-live" style={{
        border: "1px solid #86EFAC", background: "#F0FDF4", color: "#166534",
        padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 14,
      }}>
        <CheckCircle2 className="w-5 h-5" style={{ color: "#16A34A", flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>SMS is live · A2P 10DLC approved</div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>Sequences with SMS steps will send normally.</div>
        </div>
      </div>
    );
  }

  const isPending = state.kind === "pending";
  const styles = isPending
    ? { border: "1px solid #FCD34D", bg: "#FFFBEB", ink: "#78350F", accent: "#D97706" }
    : { border: "1px solid #FCA5A5", bg: "#FEF2F2", ink: "#7F1D1D", accent: "#DC2626" };

  return (
    <div data-testid="sms-banner-status" style={{
      border: `2px solid ${styles.border}`, background: styles.bg, color: styles.ink,
      padding: "18px 22px", marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      <AlertCircle className="w-5 h-5" style={{ color: styles.accent, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
          {isPending ? "SMS pending TCR approval" : "SMS blocked · action required"}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 10 }}>
          {isPending ? (
            <>Your Twilio A2P Brand is <strong>in review</strong> with The Campaign Registry.
            This is an external review (typically 2–7 business days for Sole Proprietor,
            1–3 for Standard Brand). During this window, all SMS sends return{" "}
            <code style={{ background: "rgba(0,0,0,0.06)", padding: "1px 5px" }}>error 30034</code>{" "}
            and are automatically skipped in sequences — email steps continue running normally.</>
          ) : (
            <>Twilio A2P Brand or Campaign is not registered. SMS steps in every sequence
            will fail with error 30034 until you complete the setup below.</>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12, fontFamily: "monospace" }}>
          <StepChip label="Compliance profile" ok={!!s.compliance_profile_ok} />
          <StepChip label="Messaging service"  ok={!!s.messaging_service_ok}  />
          <StepChip label="A2P Brand"          ok={!!s.brand_ok} pending={s.brand_status === "IN_REVIEW"} />
          <StepChip label="A2P Campaign"       ok={!!s.campaign_ok} pending={s.campaign_status === "IN_PROGRESS"} />
        </div>
        <div style={{ marginTop: 12, fontSize: 12 }}>
          <strong>What to do while you wait:</strong> keep running email-only sequences.
          The moment TCR approves the Brand, click <em>&ldquo;Set up A2P Campaign registration&rdquo;</em> in Twilio Console
          — SMS sends will resume automatically, no code changes needed.
        </div>
      </div>
    </div>
  );
}

function deriveState(s) {
  if (!s || s.overall === "unknown") return { kind: "blocked" };
  if (s.brand_ok && s.campaign_ok && s.messaging_service_ok) return { kind: "live" };
  const anyPending = s.brand_status === "IN_REVIEW" || s.campaign_status === "IN_PROGRESS";
  return { kind: anyPending ? "pending" : "blocked" };
}

function StepChip({ label, ok, pending }) {
  const color = ok ? "#166534" : pending ? "#B45309" : "#6B7280";
  const bg    = ok ? "#DCFCE7" : pending ? "#FEF3C7" : "#F3F4F6";
  const mark  = ok ? "✓" : pending ? "…" : "○";
  return (
    <span style={{ background: bg, color, padding: "3px 10px", border: `1px solid ${color}22` }}>
      {mark} {label}
    </span>
  );
}
